import { createContext, useContext, useMemo, type ReactNode } from 'react';
import PageWrapper from '../PageWrapper/PageWrapper';
import {
  useHelpCenterCatalog,
  type HelpCenterArticle,
  type HelpCenterCatalogStatus,
} from '../../hooks/useHelpCenterCatalog';
import {
  isHelpCenterCategory,
  type HelpCenterCategory,
} from '../../utils/helpCenterCategories';
import HelpCenterNav from './HelpCenterNav';

type HelpCenterCatalogContextValue = {
  articles: HelpCenterArticle[];
  status: HelpCenterCatalogStatus;
};

const HelpCenterCatalogContext = createContext<HelpCenterCatalogContextValue | null>(null);

export function useHelpCenterCatalogContext() {
  const value = useContext(HelpCenterCatalogContext);
  if (!value) {
    throw new Error('useHelpCenterCatalogContext must be used inside HelpCenterLayout');
  }
  return value;
}

type Props = {
  children: ReactNode;
  header?: ReactNode;
  activeCategory?: HelpCenterCategory;
  activeSlug?: string;
};

const HelpCenterLayout = ({ children, header, activeCategory, activeSlug }: Props) => {
  const catalog = useHelpCenterCatalog();
  const resolvedCategory = useMemo(() => {
    if (activeCategory) {
      return activeCategory;
    }
    if (!activeSlug) {
      return undefined;
    }
    const article = catalog.articles.find((entry) => entry.slug === activeSlug);
    if (article?.category && isHelpCenterCategory(article.category)) {
      return article.category;
    }
    return undefined;
  }, [activeCategory, activeSlug, catalog.articles]);

  return (
    <PageWrapper>
      <HelpCenterCatalogContext.Provider value={catalog}>
        {header}
        <div className="mt-8 flex flex-col gap-10 lg:flex-row lg:items-start">
          <aside className="w-full shrink-0 lg:w-64">
            <HelpCenterNav
              articles={catalog.articles}
              status={catalog.status}
              activeCategory={resolvedCategory}
              activeSlug={activeSlug}
            />
          </aside>
          <div className="min-w-0 flex-1">{children}</div>
        </div>
      </HelpCenterCatalogContext.Provider>
    </PageWrapper>
  );
};

export default HelpCenterLayout;
